// src/hooks/useBulkJobsHistory.ts

import { useCallback, useEffect, useState } from "react";
import { useAccount } from "../context";
import { apiService } from "../services/axios";

export interface BulkJob {
  id: string;
  campaignId: string;
  templateId: string;
  templateName: string;
  parameters: any[];
  sentBy: string;
  companyId: string;
  status: "pending" | "processing" | "completed" | "failed" | "cancelled";
  progress: {
    total: number;
    sent: number;
    failed: number;
    processed: number;
  };
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  error?: string;
  duration?: number;
  successRate?: number;
  templateContent?: string;
  campaignName?: string;
}

interface BulkJobsResponse {
  jobs: BulkJob[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
    totalPages: number;
    currentPage: number;
  };
}

export const useBulkJobsHistory = () => {
  const [jobs, setJobs] = useState<BulkJob[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const { account } = useAccount();

  const limit = 10; // 10 jobs por página

  // Função para carregar jobs
  const loadJobs = useCallback(
    async (pageNum = 1, append = false) => {
      if (!account?.company_id) return;

      setLoading(true);
      setError(null);

      try {
        const offset = (pageNum - 1) * limit;
        const response = await apiService.get<BulkJobsResponse>(
          `/bulk-queue/jobs?limit=${limit}&offset=${offset}`,
          {
            params: { companyId: account.company_id },
          },
        );

        if (response.status === "success" && response.data) {
          const { jobs: newJobs, pagination: paginationData } = response.data;

          if (append) {
            setJobs((prev) => [...prev, ...newJobs]);
          } else {
            setJobs(newJobs);
          }

          setTotal(paginationData.total);
          setHasMore(paginationData.hasMore);
          setTotalPages(paginationData.totalPages);
          setPage(pageNum);
        } else {
          setError("Erro ao carregar histórico de jobs");
        }
      } catch (err) {
        console.error("Erro ao carregar jobs:", err);
        setError("Erro ao carregar histórico de jobs");
      } finally {
        setLoading(false);
      }
    },
    [account?.company_id, limit],
  );

  // Função para carregar mais jobs (infinite scroll)
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      loadJobs(page + 1, true);
    }
  }, [loading, hasMore, page, loadJobs]);

  // Função para ir para uma página específica
  const goToPage = useCallback(
    (pageNum: number) => {
      if (pageNum >= 1 && pageNum <= totalPages && !loading) {
        loadJobs(pageNum, false);
      }
    },
    [totalPages, loading, loadJobs],
  );

  // Função para ir para página anterior
  const goToPreviousPage = useCallback(() => {
    if (page > 1 && !loading) {
      goToPage(page - 1);
    }
  }, [page, loading, goToPage]);

  // Função para ir para próxima página
  const goToNextPage = useCallback(() => {
    if (page < totalPages && !loading) {
      goToPage(page + 1);
    }
  }, [page, totalPages, loading, goToPage]);

  // Função para atualizar/refresh
  const refresh = useCallback(() => {
    loadJobs(1, false);
  }, [loadJobs]);

  // Carregar jobs inicialmente
  useEffect(() => {
    if (account?.company_id) {
      setPage(1);
      setJobs([]);
      setHasMore(true);
      loadJobs(1, false);
    }
  }, [account?.company_id, loadJobs]);

  return {
    jobs,
    loading,
    error,
    pagination: {
      total,
      limit,
      offset: (page - 1) * limit,
      hasMore,
      totalPages,
      currentPage: page,
    },
    loadMore,
    goToPage,
    goToPreviousPage,
    goToNextPage,
    refresh,
    setError,
  };
};
